import { useMemo } from 'react';
import { useGlobalStore } from '../../stores/globalStore';
import { useAllFacilities } from '../../api/userFacilityApi';
import { useMyDefaultFacility, useSetMyDefaultFacility, useMyStartForm, useSetMyStartForm } from '../../api/usersApi';
import { usePermission } from '../../hooks/usePermission';
import { START_FORM, DESKTOP_ONLY_START_FORMS } from '../../auth/resolveStartRoute';

/**
 * The two sign-in defaults a user sets for themselves: the start screen (WinForms Options "Start
 * Form", tblPMSec_User.DF_Start_Form) and the default facility (DF_Dietech_Facility). My Settings
 * and the welcome tour both use these, so the choices offered and the calls made are the same.
 */

/** Runs a mutation and resolves true when it saved, false when it failed. */
function settle<V>(
  mutate: (vars: V, opts: { onSuccess: () => void; onError: () => void }) => void,
  vars: V,
): Promise<boolean> {
  return new Promise((resolve) => {
    mutate(vars, { onSuccess: () => resolve(true), onError: () => resolve(false) });
  });
}

export interface StartScreenOption {
  id: number;
  name: string;
}

/**
 * The start screens to offer, in the WinForms order. WinForms "None" (0) opens the resident grid, so
 * it shows as the resident list. The resident list is offered when the role can open it (or it is the
 * saved value); Selective Touch when the role opens Selective Touch at startup (dietech_Level and the
 * touch level, as resolveStartRoute) or it is saved. A purchasing start form saved in WinForms is
 * listed only while it is the saved value, since the website has no such page.
 */
export function startScreenOptions(args: {
  residentPlural: string;
  canResidents: boolean;
  canTouch: boolean;
  /** The saved value, with 0 already read as the resident list; undefined while loading. */
  current: number | undefined;
}): StartScreenOption[] {
  const { residentPlural, canResidents, canTouch, current } = args;
  const list: StartScreenOption[] = [{ id: START_FORM.DASHBOARD, name: 'Dashboard' }];
  if (canResidents || current === START_FORM.RESIDENTS) {
    list.push({ id: START_FORM.RESIDENTS, name: residentPlural });
  }
  if (canTouch || current === START_FORM.SELECTIVE_TOUCH) {
    list.push({ id: START_FORM.SELECTIVE_TOUCH, name: 'Selective Touch' });
  }
  if (current != null && DESKTOP_ONLY_START_FORMS[current]) {
    list.push({ id: current, name: `${DESKTOP_ONLY_START_FORMS[current]} (desktop only, opens ${residentPlural} here)` });
  }
  return list;
}

/** The start screen: what is saved, what may be picked, and saving a new one. */
export function useStartScreen() {
  const userId = useGlobalStore((s) => s.userId);
  const residentPlural = useGlobalStore((s) => s.residentPluralLabel) || 'Residents';
  const { getLevel, roleLoaded } = usePermission();
  const { data: saved, isLoading } = useMyStartForm(userId);
  const mutation = useSetMyStartForm(userId);

  const touchLevel = getLevel('dietech_Selective_Menu_Touch_Level');
  const touchDirectLoad = roleLoaded && (touchLevel === 1 || touchLevel === 2);
  const canTouch = getLevel('dietech_Level') > 0 && touchLevel > 0;
  const canResidents = getLevel('dietech_Resident_Level') > 0;
  const current = saved === 0 ? START_FORM.RESIDENTS : saved;

  const options = useMemo(
    () => startScreenOptions({ residentPlural, canResidents, canTouch, current }),
    [residentPlural, canResidents, canTouch, current],
  );

  /** Saves `next` when it differs from what is saved. Resolves true once saved (or nothing to do). */
  const save = async (next: number): Promise<boolean> => {
    if (isLoading || mutation.isPending) return false;
    if (next === (current ?? START_FORM.RESIDENTS)) return true;
    // useSetMyStartForm says whether it saved (a notification either way).
    return settle(mutation.mutate, { startForm: next, label: options.find((o) => o.id === next)?.name });
  };

  return { options, current, isLoading, saving: mutation.isPending, touchDirectLoad, save };
}

/** "None": the first of the user's facilities to be set up. */
export const NO_DEFAULT_FACILITY = 0;

/** The default facility: the user's own facilities, what is saved, and saving a new one. */
export function useDefaultFacility() {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const userId = useGlobalStore((s) => s.userId);
  const { data: facilities = [], isLoading: facilitiesLoading } = useAllFacilities(facilityId, userId);
  const { data: savedDefault, isLoading: defaultLoading } = useMyDefaultFacility(userId);
  const mutation = useSetMyDefaultFacility(userId);

  const options = useMemo(
    () => [
      { id: NO_DEFAULT_FACILITY, name: 'None (the first of my facilities to be set up)' },
      ...[...facilities]
        .sort((a, b) => a.facility_Name.localeCompare(b.facility_Name))
        .map((f) => ({ id: f.pKey_Facility, name: f.facility_Name })),
    ],
    [facilities],
  );

  const loading = facilitiesLoading || defaultLoading;
  const current = savedDefault ?? NO_DEFAULT_FACILITY;

  /** Saves `next` when it differs from what is saved. Resolves true once saved (or nothing to do). */
  const save = async (next: number): Promise<boolean> => {
    if (loading || mutation.isPending) return false;
    if (next === current) return true;
    // useSetMyDefaultFacility says whether it saved (a notification either way).
    return settle(mutation.mutate, { facilityId: next, facilityName: options.find((o) => o.id === next)?.name });
  };

  return { options, current, loading, saving: mutation.isPending, facilityCount: facilities.length, save };
}
